import styled, { keyframes } from "styled-components"
import { AiOutlineCloseCircle } from "react-icons/ai"
import { useAdmin } from "../../../context/admin"
import { useGlobal } from "../../../context/global"
import { useEffect, useState } from "react"
import AdminService from "../../../services/admin.service"
import { Agency, Tour } from "../../../types/common.types"
import GeneralInput from "../../common/GeneralInput"
import InputDropdown from "../../common/InputDropdown"
import Calendar from "../../common/CalendarInput"
import CountryInput from "../../common/CountryInput"
import { format } from "date-fns"

interface FormData {
    passenger_name: string
    passenger_nationality: string
    tour_id: string
    tour_date: Date
    origin_agency_id: string
    dest_agency_id: string
    traspass_price_in: number | ''
    tickets_in: number | ''
    traspass_price_out: number | ''
    tickets_out: number | ''
    notes: string
}

const EMPTY_FORM: FormData = {
    passenger_name: '',
    passenger_nationality: 'CL',
    tour_id: '',
    tour_date: new Date(),
    origin_agency_id: '',
    dest_agency_id: '',
    traspass_price_in: '',
    tickets_in: 0,
    traspass_price_out: '',
    tickets_out: 0,
    notes: ''
}

export default function RegisterReTraspaso() {
    const { setIsCreateReTraspasoModalOpen, user } = useAdmin()
    const { setMessage, setIsLoading } = useGlobal()

    const [agencies, setAgencies] = useState<Agency[]>([])
    const [tours, setTours] = useState<Tour[]>([])
    const [form, setForm] = useState<FormData>(EMPTY_FORM)

    useEffect(() => {
        async function loadData() {
            try {
                const [agenciesRes, toursRes] = await Promise.all([
                    AdminService.listAgencies(),
                    AdminService.listTours()
                ])
                setAgencies(agenciesRes.data)
                setTours(toursRes.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadData()
    }, [])

    function close() {
        setIsCreateReTraspasoModalOpen(false)
    }

    function update(field: keyof FormData, value: any) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const molanturId = user?.user?.agency_id

    // InputDropdown returns the `selector` field value (agency name, not id)
    // so form.origin_agency_id / dest_agency_id actually hold the agency NAME
    const originAgencies = agencies.filter(a =>
        a.id !== molanturId && a.name !== form.dest_agency_id
    )
    const destAgencies = agencies.filter(a =>
        a.id !== molanturId && a.name !== form.origin_agency_id
    )

    async function submit() {
        if (!form.passenger_name) return setMessage({ type: 'error', content: 'Passenger name is required' })
        if (!form.tour_id) return setMessage({ type: 'error', content: 'Tour is required' })
        if (!form.origin_agency_id) return setMessage({ type: 'error', content: 'Origin agency is required' })
        if (!form.dest_agency_id) return setMessage({ type: 'error', content: 'Destination agency is required' })
        if (form.traspass_price_in === '') return setMessage({ type: 'error', content: 'Price in is required' })
        if (form.traspass_price_out === '') return setMessage({ type: 'error', content: 'Price out is required' })

        // InputDropdown stores names — resolve to UUIDs before sending
        const originAgency = agencies.find(a => a.name === form.origin_agency_id)
        const destAgency = agencies.find(a => a.name === form.dest_agency_id)
        if (!originAgency) return setMessage({ type: 'error', content: 'Origin agency not found' })
        if (!destAgency) return setMessage({ type: 'error', content: 'Destination agency not found' })
        if (originAgency.id === destAgency.id) return setMessage({ type: 'error', content: 'Origin and destination agencies must be different' })

        setIsLoading(true)
        try {
            await AdminService.createReTraspaso({
                passenger_name: form.passenger_name,
                passenger_nationality: form.passenger_nationality || undefined,
                tour_id: form.tour_id,
                tour_date: form.tour_date,
                origin_agency_id: originAgency.id,
                dest_agency_id: destAgency.id,
                traspass_price_in: Number(form.traspass_price_in),
                tickets_in: Number(form.tickets_in) || 0,
                traspass_price_out: Number(form.traspass_price_out),
                tickets_out: Number(form.tickets_out) || 0,
                notes: form.notes || undefined
            })
            setMessage({ type: 'success', content: 'Re-traspaso registered successfully' })
            setTimeout(() => setMessage(undefined), 2500)
            setForm(EMPTY_FORM)
            close()
        } catch (error: any) {
            setMessage({ type: 'error', content: error?.response?.data || 'Error registering re-traspaso' })
            setTimeout(() => setMessage(undefined), 3000)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Main>
            <Modal>
                <div className="close" onClick={close}>
                    <AiOutlineCloseCircle />
                </div>

                <h2>Register Re-traspaso</h2>
                <p className="subtitle">Agencia A → Molantur → Agencia B</p>

                <Section>
                    <SectionTitle>Pasajero</SectionTitle>
                    <Row>
                        <GeneralInput
                            label="Passenger name *"
                            value={form.passenger_name}
                            onChange={e => update('passenger_name', e.target.value)}
                        />
                        <CountryInput
                            label="Nationality"
                            name="nationality"
                            value={form.passenger_nationality as any}
                            onChange={e => update('passenger_nationality', e.target.value)}
                        />
                    </Row>
                </Section>

                <Section>
                    <SectionTitle>Tour y Fecha</SectionTitle>
                    <Row>
                        {tours.length > 0 &&
                            <InputDropdown
                                label="Tour *"
                                options={tours}
                                selector="id"
                                keys={['id']}
                                optionDefaultHeight={35}
                                value={form.tour_id}
                                onChange={(val: string) => update('tour_id', val)}
                            />
                        }
                    </Row>
                    <Calendar
                        containerStyle={{ marginTop: '15px' }}
                        datesStyle={{ position: 'relative' }}
                        selectedDay={form.tour_date}
                        setSelectedDay={(date: Date | null) => { if (date) update('tour_date', date) }}
                        selectPastDays
                    />
                    <DateLabel>{form.tour_date ? format(form.tour_date, 'dd/MM/yyyy') : ''}</DateLabel>
                </Section>

                <Section>
                    <SectionTitle>Agencias</SectionTitle>
                    <AgencyRow>
                        <AgencyBox className="origin">
                            <span className="label">Agencia Origen (nos envía)</span>
                            {originAgencies.length > 0 &&
                                <InputDropdown
                                    label="Agencia origen *"
                                    options={originAgencies}
                                    selector="name"
                                    keys={['name']}
                                    optionDefaultHeight={35}
                                    value={form.origin_agency_id}
                                    onChange={(val: string) => update('origin_agency_id', val)}
                                />
                            }
                        </AgencyBox>
                        <Arrow>→</Arrow>
                        <AgencyBox className="molantur">
                            <span className="label">Molantur</span>
                            <div className="molantur-badge">Molantur</div>
                        </AgencyBox>
                        <Arrow>→</Arrow>
                        <AgencyBox className="dest">
                            <span className="label">Agencia Destino (nosotros enviamos)</span>
                            {destAgencies.length > 0 &&
                                <InputDropdown
                                    label="Agencia destino *"
                                    options={destAgencies}
                                    selector="name"
                                    keys={['name']}
                                    optionDefaultHeight={35}
                                    value={form.dest_agency_id}
                                    onChange={(val: string) => update('dest_agency_id', val)}
                                />
                            }
                        </AgencyBox>
                    </AgencyRow>
                </Section>

                <Section>
                    <SectionTitle>Precios</SectionTitle>
                    <PriceGrid>
                        <PriceBlock className="in">
                            <span className="price-label">Agencia origen nos paga</span>
                            <GeneralInput
                                label="Traspass price in *"
                                type="number"
                                value={form.traspass_price_in}
                                onChange={e => update('traspass_price_in', e.target.value)}
                            />
                            <GeneralInput
                                label="Tickets in"
                                type="number"
                                value={form.tickets_in}
                                onChange={e => update('tickets_in', e.target.value)}
                            />
                        </PriceBlock>
                        <PriceBlock className="out">
                            <span className="price-label">Nosotros pagamos a destino</span>
                            <GeneralInput
                                label="Traspass price out *"
                                type="number"
                                value={form.traspass_price_out}
                                onChange={e => update('traspass_price_out', e.target.value)}
                            />
                            <GeneralInput
                                label="Tickets out"
                                type="number"
                                value={form.tickets_out}
                                onChange={e => update('tickets_out', e.target.value)}
                            />
                        </PriceBlock>
                    </PriceGrid>
                    {form.traspass_price_in !== '' && form.traspass_price_out !== '' &&
                        <Margin positive={Number(form.traspass_price_in) + Number(form.tickets_in) - Number(form.traspass_price_out) - Number(form.tickets_out) >= 0}>
                            Margen Molantur: ${(
                                Number(form.traspass_price_in) +
                                Number(form.tickets_in) -
                                Number(form.traspass_price_out) -
                                Number(form.tickets_out)
                            ).toLocaleString('es-CL')}
                        </Margin>
                    }
                </Section>

                <Section>
                    <GeneralInput
                        label="Notes (optional)"
                        value={form.notes}
                        onChange={e => update('notes', e.target.value)}
                    />
                </Section>

                <button type="button" onClick={submit}>
                    Register Re-traspaso
                </button>
            </Modal>
        </Main>
    )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const showUp = keyframes`
    from { top: 100%; }
    to   { top: 50%; }
`

const Main = styled.div`
    z-index: 100;
    position: fixed;
    top: 0; left: 0;
    width: 100vw;
    height: 100vh;
    background-color: ${p => p.theme.blackTransparent};
`

const Modal = styled.div`
    width: 90%;
    max-width: 620px;
    max-height: 85vh;
    overflow-y: auto;
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${p => p.theme.white};
    box-shadow: 0 0 20px ${p => p.theme.blackTransparent};
    padding: 30px 25px;
    border-radius: 10px;
    animation: ${showUp} .3s ease forwards;

    h2 {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 4px;
        color: ${p => p.theme.blueDark};
    }
    .subtitle {
        font-size: 13px;
        color: ${p => p.theme.blueLight};
        margin: 0 0 20px;
        letter-spacing: 1px;
    }
    .close {
        position: absolute;
        right: 18px; top: 18px;
        font-size: 22px;
        cursor: pointer;
        color: ${p => p.theme.black};
    }
    button[type=button] {
        width: 100%;
        margin-top: 10px;
        height: 2.6em;
        font-size: 16px;
        background-color: ${p => p.theme.white};
        color: ${p => p.theme.blue};
        border: 2px solid ${p => p.theme.blue};
        border-radius: 6px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        z-index: 1;
        transition: color .3s;
        &::before {
            content: "";
            position: absolute;
            background: ${p => p.theme.blue};
            width: calc(100% + 100px);
            height: 200px;
            border-radius: 50%;
            z-index: -1;
            top: 100%; left: 100%;
            transition: .3s all;
        }
        &:hover { color: ${p => p.theme.white}; }
        &:hover::before { top: -30px; left: -30%; }
    }
`

const Section = styled.div`
    margin-bottom: 20px;
`

const SectionTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${p => p.theme.blueLight};
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 10px;
    padding-bottom: 4px;
    border-bottom: 1px solid ${p => p.theme.blueLight};
`

const Row = styled.div`
    display: flex;
    gap: 12px;
    > * { flex: 1; }
`

const AgencyRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    flex-wrap: wrap;
`

const AgencyBox = styled.div`
    flex: 1;
    min-width: 140px;
    .label {
        display: block;
        font-size: 11px;
        color: #888;
        margin-bottom: 4px;
    }
    &.molantur {
        flex: 0 0 90px;
        text-align: center;
        .molantur-badge {
            background-color: ${p => p.theme.blueDark};
            color: ${p => p.theme.white};
            padding: 8px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 600;
        }
    }
`

const Arrow = styled.span`
    font-size: 22px;
    color: ${p => p.theme.blueLight};
    padding-bottom: 4px;
    align-self: flex-end;
`

const PriceGrid = styled.div`
    display: flex;
    gap: 16px;
    > * { flex: 1; }
`

const PriceBlock = styled.div`
    padding: 12px;
    border-radius: 8px;
    .price-label {
        display: block;
        font-size: 11px;
        font-weight: 600;
        margin-bottom: 8px;
    }
    &.in {
        background-color: ${p => p.theme.greenLight || '#f0faf0'};
        .price-label { color: ${p => p.theme.greenDark || '#1e6b3a'}; }
    }
    &.out {
        background-color: ${p => p.theme.lightRedFade || '#fff0f0'};
        .price-label { color: ${p => p.theme.lightRed || '#c0392b'}; }
    }
`

const Margin = styled.div<{ positive: boolean }>`
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    background-color: ${p => p.positive ? (p.theme.greenLight || '#f0faf0') : (p.theme.lightRedFade || '#fff0f0')};
    color: ${p => p.positive ? (p.theme.greenDark || '#1e6b3a') : (p.theme.lightRed || '#c0392b')};
`

const DateLabel = styled.div`
    font-size: 13px;
    color: #666;
    margin-top: 6px;
`
